import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '../../../utils/firebase';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import InputField from '../../../components/ui/InputField';
import DecoratedCakePreview from './birthdayRollingPaper/DecoratedCakePreview';
import {
  CAKE_SHEETS,
  CAKE_TOPPING_SLOT_POSITIONS,
  CAKE_TOPPINGS,
  DEFAULT_CAKE_DESIGN,
  MAX_PLACED_TOPPINGS,
  clampToppingViewCoords,
  parseCakeDesign,
  serializeCakeDesign,
  type CakeDesign,
} from './birthdayRollingPaper/cakeTypes';

/** 시트 캐러셀 슬롯·간격·스케일 — `carouselInnerW`는 뷰포트(또는 창) 너비 기준 */
function getSheetCarouselLayout(carouselInnerW: number) {
  const vw = Math.max(260, carouselInnerW);
  const narrow = vw < 400;
  const tight = vw < 520;
  const w = Math.round(Math.min(200, Math.max(116, vw * 0.42)));
  const g = narrow ? 10 : tight ? 14 : 22;
  const selectedScale = narrow ? 1.12 : tight ? 1.2 : 1.26;
  const sideScale = narrow ? 0.88 : tight ? 0.85 : 0.82;
  return { w, g, selectedScale, sideScale };
}

const PAPER_ID_PATTERN = /^[a-f0-9]{64}$/;

function generateClientTimestamp(): Timestamp {
  return Timestamp.now();
}

interface PaperMeta {
  recipientName: string;
  studentNumber?: number;
}

interface MessageRow {
  id: string;
  authorName: string;
  text: string;
  createdAt: Date | null;
  cakeDesign: CakeDesign;
}

const PublicBirthdayRollingPaper: React.FC = () => {
  const { paperId } = useParams<{ paperId: string }>();
  const [paper, setPaper] = useState<PaperMeta | null>(null);
  const [paperMissing, setPaperMissing] = useState(false);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [cakeDesign, setCakeDesign] = useState<CakeDesign>({ ...DEFAULT_CAKE_DESIGN });
  const [authorName, setAuthorName] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openLetter, setOpenLetter] = useState<MessageRow | null>(null);
  /** 0 시트 → 1 장식 → 2 이름 */
  const [decorateStep, setDecorateStep] = useState<0 | 1 | 2>(0);
  const [cakeWizardDone, setCakeWizardDone] = useState(false);

  const idOk = useMemo(() => !!paperId && PAPER_ID_PATTERN.test(paperId), [paperId]);

  useEffect(() => {
    if (!openLetter) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenLetter(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openLetter]);

  useEffect(() => {
    if (!paperId || !idOk) {
      setPaperMissing(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, COLLECTIONS.BIRTHDAY_ROLLING_PAPERS, paperId));
        if (cancelled) return;
        if (!snap.exists()) {
          setPaperMissing(true);
          return;
        }
        const d = snap.data();
        setPaper({
          recipientName: (d.recipientName as string) || '친구',
          studentNumber: typeof d.studentNumber === 'number' ? d.studentNumber : undefined,
        });
        setPaperMissing(false);
      } catch (e) {
        console.error(e);
        if (!cancelled) setPaperMissing(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paperId, idOk]);

  useEffect(() => {
    if (!paperId || !idOk) return;
    const col = collection(db, COLLECTIONS.BIRTHDAY_ROLLING_PAPERS, paperId, 'messages');
    const q = query(col, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: MessageRow[] = snap.docs.map((d) => {
          const data = d.data();
          const ca = data.createdAt;
          let createdAt: Date | null = null;
          if (ca instanceof Timestamp) createdAt = ca.toDate();
          else if (ca && typeof (ca as { toDate?: () => Date }).toDate === 'function') {
            createdAt = (ca as { toDate: () => Date }).toDate();
          }
          return {
            id: d.id,
            authorName: (data.authorName as string) || '',
            text: (data.text as string) || '',
            createdAt,
            cakeDesign: parseCakeDesign(data.cakeDesign),
          };
        });
        setMessages(rows);
      },
      (err) => {
        console.error(err);
        setError('메시지를 불러오지 못했습니다.');
      }
    );
    return () => unsub();
  }, [paperId, idOk]);

  const placeToppingFromPalette = useCallback((toppingId: string) => {
    if (!CAKE_TOPPINGS.some((t) => t.id === toppingId)) return;
    setCakeDesign((prev) => {
      if (prev.toppings.length >= MAX_PLACED_TOPPINGS) return prev;
      const n = prev.toppings.length;
      const slot = CAKE_TOPPING_SLOT_POSITIONS[n % CAKE_TOPPING_SLOT_POSITIONS.length] ?? { x: 0, y: 6 };
      const layer = Math.floor(n / CAKE_TOPPING_SLOT_POSITIONS.length);
      const c = clampToppingViewCoords(slot.x + layer * 4, slot.y - layer * 5);
      return { ...prev, toppings: [...prev.toppings, { id: toppingId, x: c.x, y: c.y }] };
    });
  }, []);

  const removeLastTopping = useCallback(() => {
    setCakeDesign((prev) => {
      if (prev.toppings.length === 0) return prev;
      return { ...prev, toppings: prev.toppings.slice(0, -1) };
    });
  }, []);

  const clearAllToppings = useCallback(() => {
    setCakeDesign((prev) => ({ ...prev, toppings: [] }));
  }, []);

  const sheetCarouselIndex = useMemo(
    () => Math.max(0, CAKE_SHEETS.findIndex((s) => s.id === cakeDesign.sheetId)),
    [cakeDesign.sheetId]
  );

  /** 시트 캐러셀 무한 루프: [마지막 클론, …원본, 첫 클론] — 항목 2개 이상일 때만 */
  const sheetCarouselExtended = useMemo(() => {
    const n = CAKE_SHEETS.length;
    if (n <= 1) {
      return CAKE_SHEETS.map((sheet, i) => ({ sheet, extIndex: i, reactKey: `${sheet.id}-solo` }));
    }
    return [
      { sheet: CAKE_SHEETS[n - 1], extIndex: 0, reactKey: 'sheet-loop-left' },
      ...CAKE_SHEETS.map((sheet, i) => ({ sheet, extIndex: i + 1, reactKey: `sheet-loop-${i}-${sheet.id}` })),
      { sheet: CAKE_SHEETS[0], extIndex: n + 1, reactKey: 'sheet-loop-right' },
    ];
  }, []);

  const sheetLoopN = CAKE_SHEETS.length;
  /** 확장 트랙에서의 슬라이드 위치(1…n = 실제 시트). 클론은 0, n+1 */
  const [sheetStripSlideIndex, setSheetStripSlideIndex] = useState(1);
  const sheetStripSlideIndexRef = useRef(1);
  const [sheetStripSkipTransition, setSheetStripSkipTransition] = useState(false);
  useLayoutEffect(() => {
    sheetStripSlideIndexRef.current = sheetStripSlideIndex;
  }, [sheetStripSlideIndex]);

  const decorateStepPrevRef = useRef<0 | 1 | 2>(decorateStep);

  const selectSheetAtExtIndex = useCallback(
    (extIndex: number) => {
      if (sheetLoopN <= 1) {
        setCakeDesign((d) => ({ ...d, sheetId: CAKE_SHEETS[0]?.id ?? d.sheetId }));
        return;
      }
      const row = sheetCarouselExtended[extIndex];
      if (!row) return;
      setCakeDesign((d) => ({ ...d, sheetId: row.sheet.id }));
      if (extIndex === 0 || extIndex === sheetLoopN + 1) {
        flushSync(() => {
          setSheetStripSkipTransition(true);
          setSheetStripSlideIndex(extIndex === 0 ? sheetLoopN : 1);
        });
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setSheetStripSkipTransition(false));
        });
      } else {
        setSheetStripSlideIndex(extIndex);
      }
    },
    [sheetCarouselExtended, sheetLoopN]
  );

  useLayoutEffect(() => {
    const prev = decorateStepPrevRef.current;
    decorateStepPrevRef.current = decorateStep;
    if (decorateStep === 0 && prev !== 0 && sheetLoopN > 1) {
      setSheetStripSlideIndex(Math.max(1, Math.min(sheetLoopN, sheetCarouselIndex + 1)));
      setSheetStripSkipTransition(false);
    }
  }, [decorateStep, sheetCarouselIndex, sheetLoopN]);

  const sheetStripViewportRef = useRef<HTMLDivElement>(null);
  const [sheetStripViewportW, setSheetStripViewportW] = useState(0);
  const [layoutViewportW, setLayoutViewportW] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 880)
  );
  const sheetTouchStartX = useRef<number | null>(null);

  useEffect(() => {
    const onResize = () => setLayoutViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isCompact = layoutViewportW <= 768;

  const sheetCarouselLayout = useMemo(() => {
    const inner =
      sheetStripViewportW > 8 ? sheetStripViewportW : Math.max(280, layoutViewportW - (isCompact ? 56 : 80));
    return getSheetCarouselLayout(inner);
  }, [sheetStripViewportW, layoutViewportW, isCompact]);

  useLayoutEffect(() => {
    if (decorateStep !== 0) return;
    const el = sheetStripViewportRef.current;
    if (!el) return;
    const measure = () => setSheetStripViewportW(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [decorateStep]);

  const sheetStripTranslateX = useMemo(() => {
    const vwMeasured = sheetStripViewportW;
    const { w: W, g: G } = sheetCarouselLayout;
    const vw =
      vwMeasured > 8 ? vwMeasured : Math.max(W + G + 8, layoutViewportW - (isCompact ? 56 : 80));
    if (vw < 8) return 0;
    const slidePos = sheetLoopN <= 1 ? sheetCarouselIndex : sheetStripSlideIndex;
    const centerI = slidePos * (W + G) + W / 2;
    return vw / 2 - centerI;
  }, [
    sheetStripViewportW,
    sheetCarouselIndex,
    sheetCarouselLayout,
    layoutViewportW,
    isCompact,
    sheetStripSlideIndex,
    sheetLoopN,
  ]);

  const sheetLoopSnapRef = useRef(false);

  const onSheetStripTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (sheetStripSkipTransition) return;
      if (e.propertyName !== 'transform') return;
      if (e.target !== e.currentTarget) return;
      if (sheetLoopN <= 1) return;
      const si = sheetStripSlideIndexRef.current;
      if (si === sheetLoopN + 1) {
        if (sheetLoopSnapRef.current) return;
        sheetLoopSnapRef.current = true;
        requestAnimationFrame(() => {
          flushSync(() => {
            setSheetStripSkipTransition(true);
            setSheetStripSlideIndex(1);
          });
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setSheetStripSkipTransition(false);
              sheetLoopSnapRef.current = false;
            });
          });
        });
      } else if (si === 0) {
        if (sheetLoopSnapRef.current) return;
        sheetLoopSnapRef.current = true;
        requestAnimationFrame(() => {
          flushSync(() => {
            setSheetStripSkipTransition(true);
            setSheetStripSlideIndex(sheetLoopN);
          });
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setSheetStripSkipTransition(false);
              sheetLoopSnapRef.current = false;
            });
          });
        });
      }
    },
    [sheetStripSkipTransition, sheetLoopN]
  );

  const editCakePreviewSize = useMemo(() => {
    const inner = Math.max(260, layoutViewportW - (isCompact ? 48 : 96));
    return Math.min(228, Math.max(148, Math.round(inner * 0.58)));
  }, [layoutViewportW, isCompact]);

  const nameStepPreviewSize = useMemo(
    () => Math.max(120, Math.min(190, Math.round(editCakePreviewSize * 0.92))),
    [editCakePreviewSize]
  );

  const messageCakeSize = isCompact ? 118 : 140;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paperId || !idOk) return;
    if (!cakeWizardDone) {
      setError('케이크 꾸미기(시트·장식·이름)를 끝까지 완료해 주세요.');
      return;
    }
    const name = authorName.trim();
    const body = text.trim();
    if (!name) {
      setError('이름(닉네임)을 입력해 주세요.');
      return;
    }
    if (!body) {
      setError('편지 내용을 입력해 주세요.');
      return;
    }
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await addDoc(collection(db, COLLECTIONS.BIRTHDAY_ROLLING_PAPERS, paperId, 'messages'), {
        authorName: name,
        text: body,
        createdAt: generateClientTimestamp(),
        cakeDesign: serializeCakeDesign(cakeDesign),
      });
      setText('');
      setSuccess('케이크 편지가 전달되었습니다.');
      setDecorateStep(0);
      setCakeWizardDone(false);
      setCakeDesign({ ...DEFAULT_CAKE_DESIGN });
    } catch (err) {
      console.error(err);
      setError('등록에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!paperId || !idOk) {
    return (
      <div style={{ padding: 24, maxWidth: 560, margin: '0 auto', fontFamily: 'var(--font-family-primary)' }}>
        <p style={{ color: 'var(--Color-text-normal)' }}>올바르지 않은 링크입니다.</p>
      </div>
    );
  }

  if (paperMissing) {
    return (
      <div style={{ padding: 24, maxWidth: 560, margin: '0 auto', fontFamily: 'var(--font-family-primary)' }}>
        <p style={{ color: 'var(--Color-text-normal)' }}>롤링페이퍼를 찾을 수 없거나 만료되었습니다.</p>
      </div>
    );
  }

  const titleSuffix =
    paper?.studentNumber != null && paper.studentNumber > 0
      ? `${paper.studentNumber}번 ${paper.recipientName}`
      : paper?.recipientName ?? '';

  return (
    <div
      style={{
        padding: isCompact ? '10px' : 'var(--gap-level-4)',
        maxWidth: 880,
        width: '100%',
        boxSizing: 'border-box',
        margin: '0 auto',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
        minHeight: 'calc(100vh - 80px)',
        overflowX: 'hidden',
      }}
    >
      <Card
        padding={isCompact ? '14px 12px' : '24px'}
        borderRadius="16px"
        background="var(--Color-background-normal)"
        style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
      >
        <h1
          style={{
            margin: '0 0 8px 0',
            fontSize: 'clamp(18px, 4.8vw, 22px)',
            fontWeight: 600,
            color: 'var(--Color-text-strong)',
            lineHeight: 1.35,
          }}
        >
          생일 축하 케이크 롤링페이퍼
        </h1>
        <p
          style={{
            margin: '0 0 24px 0',
            fontSize: 'clamp(14px, 3.2vw, 15px)',
            color: 'var(--Color-text-normal)',
            lineHeight: 1.55,
            wordBreak: 'keep-all',
          }}
        >
          받는 분: <strong>{titleSuffix}</strong> 님을 위해 케이크를 꾸민 뒤, 편지를 남겨 주세요. 아래 케이크를 누르면 편지를 읽을 수 있어요.
        </p>

        <form onSubmit={handleSubmit} style={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
          <section style={{ marginBottom: 28, minWidth: 0, width: '100%', maxWidth: '100%' }}>
            <h2
              style={{
                margin: '0 0 20px 0',
                fontSize: 'clamp(22px, 4.5vw, 26px)',
                fontWeight: 700,
                color: '#3D2914',
                letterSpacing: '-0.02em',
              }}
            >
              {decorateStep === 0 ? '1. 시트 고르기' : decorateStep === 1 ? '2. 장식 올리기' : '3. 이름 붙이기'}
            </h2>

            {decorateStep === 0 ? (
              <>
                <p
                  style={{
                    margin: '0 0 18px 0',
                    fontSize: 'clamp(15px, 2.8vw, 17px)',
                    color: 'var(--Color-text-normal)',
                    lineHeight: 1.55,
                  }}
                >
                  옆으로 밀어 시트를 바꿀 수 있어요. 원하는 케이크를 눌러도 선택됩니다.
                </p>
                <div
                  ref={sheetStripViewportRef}
                  onTouchStart={(e) => {
                    sheetTouchStartX.current = e.touches[0]?.clientX ?? null;
                  }}
                  onTouchEnd={(e) => {
                    const start = sheetTouchStartX.current;
                    sheetTouchStartX.current = null;
                    if (start == null) return;
                    const dx = (e.changedTouches[0]?.clientX ?? start) - start;
                    const threshold = isCompact ? 36 : 48;
                    if (Math.abs(dx) < threshold) return;
                    if (sheetLoopN <= 1) {
                      setCakeDesign((d) => {
                        const cur = Math.max(0, CAKE_SHEETS.findIndex((s) => s.id === d.sheetId));
                        const n = CAKE_SHEETS.length;
                        const next = dx < 0 ? (cur + 1) % n : (cur - 1 + n) % n;
                        return { ...d, sheetId: CAKE_SHEETS[next].id };
                      });
                      return;
                    }
                    const si = sheetStripSlideIndexRef.current;
                    const nextSi = Math.max(0, Math.min(sheetLoopN + 1, dx < 0 ? si + 1 : si - 1));
                    const row = sheetCarouselExtended[nextSi];
                    if (row) {
                      setCakeDesign((d) => ({ ...d, sheetId: row.sheet.id }));
                    }
                    setSheetStripSlideIndex(nextSi);
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    margin: '0 auto 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    paddingTop: isCompact ? 16 : 18,
                    paddingBottom: isCompact ? 10 : 14,
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    minHeight:
                      (sheetCarouselLayout.w * sheetCarouselLayout.selectedScale * 220) / 200 +
                      (isCompact ? 40 : 52),
                    touchAction: 'pan-y',
                  }}
                >
                  <div
                    onTransitionEnd={onSheetStripTransitionEnd}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-end',
                      justifyContent: 'flex-start',
                      gap: sheetCarouselLayout.g,
                      width: 'max-content',
                      transform: `translateX(${sheetStripTranslateX}px)`,
                      transition: sheetStripSkipTransition
                        ? 'none'
                        : 'transform 0.45s cubic-bezier(0.25, 0.85, 0.35, 1)',
                      willChange: 'transform',
                    }}
                  >
                    {sheetCarouselExtended.map((entry) => {
                      const selected =
                        sheetLoopN <= 1
                          ? entry.sheet.id === cakeDesign.sheetId
                          : entry.extIndex === sheetStripSlideIndex;
                      return (
                        <button
                          key={entry.reactKey}
                          type="button"
                          onClick={() => selectSheetAtExtIndex(entry.extIndex)}
                          aria-current={selected ? 'true' : undefined}
                          aria-label={`${entry.sheet.label} 시트 선택`}
                          style={{
                            position: 'relative',
                            flex: `0 0 ${sheetCarouselLayout.w}px`,
                            width: sheetCarouselLayout.w,
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            lineHeight: 0,
                          }}
                        >
                          <div
                            style={{
                              position: 'relative',
                              transform: selected
                                ? `scale(${sheetCarouselLayout.selectedScale})`
                                : `scale(${sheetCarouselLayout.sideScale})`,
                              opacity: selected ? 1 : 0.72,
                              /* 스트립 translate와 동시에 scale도 애니메이션하면 루프 스냅(끝→처음)에서 끊김 — 시트 단계만 즉시 반영 */
                              transition: 'none',
                              transformOrigin: '50% 100%',
                            }}
                          >
                            {entry.sheet.sheetBadge === 'hot' ? (
                              <span
                                style={{
                                  position: 'absolute',
                                  top: -6,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: '#1a1a1a',
                                  color: '#fff',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  letterSpacing: '0.06em',
                                  padding: '4px 9px',
                                  borderRadius: 999,
                                  lineHeight: 1,
                                  whiteSpace: 'nowrap',
                                  zIndex: 2,
                                  pointerEvents: 'none',
                                }}
                              >
                                HOT
                              </span>
                            ) : null}
                            <DecoratedCakePreview
                              design={{
                                sheetId: entry.sheet.id,
                                toppings: [],
                                nameOnCake: '',
                              }}
                              size={sheetCarouselLayout.w}
                              bare
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginBottom: 22 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'clamp(14px, 2.6vw, 16px)',
                      fontWeight: 700,
                      color: '#3D2914',
                      marginBottom: 6,
                    }}
                  >
                    ▲ 이걸로 할게요!
                  </span>
                  <span style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', fontWeight: 600, color: 'var(--Color-text-normal)' }}>
                    {CAKE_SHEETS[sheetCarouselIndex]?.label}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isCompact ? 'column' : 'row',
                    alignItems: 'stretch',
                    justifyContent: 'space-between',
                    gap: isCompact ? 10 : 16,
                  }}
                >
                  <button
                    type="button"
                    disabled
                    aria-disabled
                    style={{
                      padding: '12px 22px',
                      borderRadius: 14,
                      border: '1px solid #2C2C2C',
                      background: '#fff',
                      color: '#2C2C2C',
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: 'not-allowed',
                      opacity: 0.45,
                      width: isCompact ? '100%' : 'auto',
                    }}
                  >
                    이전
                  </button>
                  <button
                    type="button"
                    onClick={() => setDecorateStep(1)}
                    style={{
                      flex: isCompact ? 'none' : 1,
                      width: isCompact ? '100%' : undefined,
                      minWidth: isCompact ? 0 : 160,
                      maxWidth: isCompact ? '100%' : 320,
                      marginLeft: isCompact ? 0 : 'auto',
                      padding: '15px 28px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#1a1a1a',
                      color: '#fff',
                      fontSize: 17,
                      fontWeight: 700,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: 'pointer',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                    }}
                  >
                    다음
                  </button>
                </div>
              </>
            ) : null}

            {decorateStep === 1 ? (
              <>
                <p
                  style={{
                    margin: '0 0 8px 0',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong)',
                  }}
                >
                  장식 올리기
                </p>
                <p
                  style={{
                    margin: '0 0 16px 0',
                    fontSize: 'clamp(13px, 3.2vw, 14px)',
                    color: 'var(--Color-text-normal)',
                    lineHeight: 1.55,
                  }}
                >
                  버튼을 누르면 케이크에 장식이 올라가요(최대 {MAX_PLACED_TOPPINGS}개). 잘못 올렸으면{' '}
                  <strong>마지막 하나 빼기</strong>로 되돌리거나 <strong>장식 전부 지우기</strong>를 눌러 주세요.
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 12,
                    width: '100%',
                  }}
                >
                  {CAKE_TOPPINGS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => placeToppingFromPalette(t.id)}
                      style={{
                        flex: '1 1 160px',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        padding: '16px 14px',
                        borderRadius: 14,
                        border: '1px solid var(--Color-line-normal)',
                        background: 'var(--Color-background-normal)',
                        color: 'var(--Color-text-strong)',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: 600,
                        fontFamily: 'var(--font-family-primary)',
                      }}
                    >
                      {t.toppingImageUrl ? (
                        <img
                          src={t.toppingImageUrl}
                          alt=""
                          width={72}
                          height={72}
                          draggable={false}
                          style={{ objectFit: 'contain' }}
                        />
                      ) : null}
                      <span>{t.label} 올리기</span>
                    </button>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginBottom: 16,
                    alignItems: 'center',
                  }}
                >
                  <button
                    type="button"
                    onClick={removeLastTopping}
                    disabled={cakeDesign.toppings.length === 0}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 10,
                      border: '1px solid var(--Color-line-normal)',
                      background: cakeDesign.toppings.length === 0 ? 'var(--Color-background-main)' : '#fff',
                      color: 'var(--Color-text-strong)',
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: cakeDesign.toppings.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: cakeDesign.toppings.length === 0 ? 0.55 : 1,
                    }}
                  >
                    마지막 하나 빼기
                  </button>
                  <button
                    type="button"
                    onClick={clearAllToppings}
                    disabled={cakeDesign.toppings.length === 0}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 10,
                      border: '1px solid var(--Color-line-normal)',
                      background: cakeDesign.toppings.length === 0 ? 'var(--Color-background-main)' : '#fff',
                      color: 'var(--Color-text-normal)',
                      fontSize: '14px',
                      fontWeight: 500,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: cakeDesign.toppings.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: cakeDesign.toppings.length === 0 ? 0.55 : 1,
                    }}
                  >
                    장식 전부 지우기
                  </button>
                </div>
                <div
                  style={{
                    marginBottom: 12,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                    width: '100%',
                    minWidth: 0,
                    /* 장식 단계: 미리보기 카드 높이 안에서 케이크를 아래로 내림(위쪽 여백) */
                    minHeight: Math.round((editCakePreviewSize * 220) / 200 + 16 + 44),
                    boxSizing: 'border-box',
                  }}
                >
                  <DecoratedCakePreview design={cakeDesign} size={editCakePreviewSize} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isCompact ? 'column' : 'row',
                    flexWrap: isCompact ? 'nowrap' : 'wrap',
                    gap: isCompact ? 10 : 12,
                    alignItems: 'stretch',
                    justifyContent: 'space-between',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setDecorateStep(0)}
                    style={{
                      padding: '12px 22px',
                      borderRadius: 14,
                      border: '1px solid #2C2C2C',
                      background: '#fff',
                      color: '#2C2C2C',
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: 'pointer',
                      width: isCompact ? '100%' : 'auto',
                    }}
                  >
                    이전
                  </button>
                  <button
                    type="button"
                    onClick={() => setDecorateStep(2)}
                    style={{
                      flex: isCompact ? 'none' : 1,
                      width: isCompact ? '100%' : undefined,
                      minWidth: isCompact ? 0 : 140,
                      maxWidth: isCompact ? '100%' : 300,
                      marginLeft: isCompact ? 0 : 'auto',
                      padding: '15px 28px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#1a1a1a',
                      color: '#fff',
                      fontSize: 17,
                      fontWeight: 700,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: 'pointer',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                    }}
                  >
                    다음
                  </button>
                </div>
              </>
            ) : null}

            {decorateStep === 2 ? (
              <>
                <p
                  style={{
                    margin: '0 0 8px 0',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'var(--Color-text-strong)',
                  }}
                >
                  케이크에 붙일 이름을 적어 주세요
                </p>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--Color-text-normal)' }}>
                  케이크 위에 쓰일 짧은 문구입니다(최대 24자).
                </p>
                <input
                  type="text"
                  value={cakeDesign.nameOnCake}
                  onChange={(e) =>
                    setCakeDesign((d) => ({ ...d, nameOnCake: e.target.value.slice(0, 24) }))
                  }
                  placeholder="예: 민수야 생일 축하해"
                  maxLength={24}
                  aria-label="케이크에 붙일 이름"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: '1px solid var(--Color-line-normal)',
                    fontFamily: 'var(--font-family-primary)',
                    fontSize: 'max(16px, 0.95rem)',
                    marginBottom: 16,
                  }}
                />
                <div style={{ marginBottom: 16, width: '100%', minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: 8, color: 'var(--Color-text-strong)' }}>
                    미리보기
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <DecoratedCakePreview design={cakeDesign} size={nameStepPreviewSize} />
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: isCompact ? 'column' : 'row',
                    flexWrap: isCompact ? 'nowrap' : 'wrap',
                    gap: isCompact ? 10 : 12,
                    alignItems: 'stretch',
                    justifyContent: 'space-between',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setDecorateStep(1)}
                    style={{
                      padding: '12px 22px',
                      borderRadius: 14,
                      border: '1px solid #2C2C2C',
                      background: '#fff',
                      color: '#2C2C2C',
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: 'pointer',
                      width: isCompact ? '100%' : 'auto',
                    }}
                  >
                    이전
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCakeWizardDone(true);
                    }}
                    style={{
                      flex: isCompact ? 'none' : 1,
                      width: isCompact ? '100%' : undefined,
                      minWidth: isCompact ? 0 : 140,
                      maxWidth: isCompact ? '100%' : 300,
                      marginLeft: isCompact ? 0 : 'auto',
                      padding: '15px 28px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#1a1a1a',
                      color: '#fff',
                      fontSize: 17,
                      fontWeight: 700,
                      fontFamily: 'var(--font-family-primary)',
                      cursor: 'pointer',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                    }}
                  >
                    완료
                  </button>
                </div>
              </>
            ) : null}
          </section>

          {cakeWizardDone ? (
            <section style={{ marginBottom: 20 }}>
              <h2
                style={{
                  margin: '0 0 12px 0',
                  fontSize: '17px',
                  fontWeight: 600,
                  color: 'var(--Color-text-strong)',
                }}
              >
                2. 편지 쓰기
              </h2>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                케이크 꾸미기를 마쳤습니다. 이제 편지를 남겨 주세요.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-level-3)' }}>
                <InputField label="이름(닉네임)" value={authorName} onChange={setAuthorName} placeholder="예: 12번" size="medium" />
                <div>
                  <label htmlFor="rolling-msg" style={{ display: 'block', marginBottom: 8, fontSize: 14, color: 'var(--Color-text-strong)' }}>
                    편지 내용
                  </label>
                  <textarea
                    id="rolling-msg"
                    value={text}
                    onChange={(ev) => setText(ev.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="마음을 담아 축하 메시지를 적어 주세요."
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '12px 14px',
                      borderRadius: 8,
                      border: '1px solid var(--Color-line-normal)',
                      fontFamily: 'var(--font-family-primary)',
                      fontSize: 'max(16px, 0.95rem)',
                      resize: 'vertical',
                    }}
                  />
                </div>
              </div>
            </section>
          ) : null}

          {error ? (
            <p role="alert" style={{ margin: '0 0 12px 0', fontSize: 14, color: '#c62828' }}>
              {error}
            </p>
          ) : null}
          {success ? (
            <p role="status" style={{ margin: '0 0 12px 0', fontSize: 14, color: 'var(--Color-fill-brand-color)' }}>
              {success}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            size="medium"
            disabled={submitting || !cakeWizardDone}
            width={isCompact ? '100%' : undefined}
          >
            {submitting ? '보내는 중…' : '케이크 편지 보내기'}
          </Button>
        </form>
      </Card>

      <div style={{ marginTop: 'var(--gap-level-4)' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)', marginBottom: 12 }}>
          친구들이 보낸 케이크
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--Color-text-normal)', marginTop: 0, marginBottom: 16 }}>
          케이크를 누르면 편지가 열립니다.
        </p>
        {messages.length === 0 ? (
          <p style={{ color: 'var(--Color-text-normal)', fontSize: 14 }}>아직 케이크가 없습니다.</p>
        ) : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: isCompact ? 12 : 16,
              alignItems: 'flex-start',
              justifyContent: isCompact ? 'center' : 'flex-start',
              width: '100%',
              minWidth: 0,
            }}
          >
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  flex: isCompact ? '1 1 calc(50% - 6px)' : '0 0 auto',
                  maxWidth: isCompact ? 'calc(50% - 6px)' : 200,
                  minWidth: 0,
                }}
              >
                <DecoratedCakePreview
                  design={m.cakeDesign}
                  size={messageCakeSize}
                  interactive
                  onClick={() => setOpenLetter(m)}
                  ariaLabel={`${m.authorName}님의 편지 열기`}
                />
                <span style={{ fontSize: '12px', color: 'var(--Color-text-normal)', textAlign: 'center' }}>
                  {m.authorName}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {openLetter ? (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(13,13,13,0.45)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isCompact ? 12 : 16,
          }}
          onClick={() => setOpenLetter(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="letter-dialog-title"
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 420, minWidth: 0 }}
          >
            <Card
              padding={isCompact ? '16px 14px' : '24px'}
              borderRadius="16px"
              background="var(--Color-background-normal)"
              style={{
                maxWidth: 420,
                width: '100%',
                maxHeight: 'min(88vh, 620px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
                <DecoratedCakePreview design={openLetter.cakeDesign} size={120} />
                <h3 id="letter-dialog-title" style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
                  {openLetter.authorName}님의 편지
                </h3>
                {openLetter.createdAt ? (
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--Color-text-normal)' }}>
                    {openLetter.createdAt.toLocaleString('ko-KR')}
                  </p>
                ) : null}
                <p
                  style={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    fontSize: '15px',
                    lineHeight: 1.6,
                    color: 'var(--Color-text-normal)',
                    width: '100%',
                  }}
                >
                  {openLetter.text}
                </p>
                <Button variant="primary" size="medium" onClick={() => setOpenLetter(null)}>
                  닫기
                </Button>
              </div>
            </Card>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PublicBirthdayRollingPaper;
